import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { patientAttendanceApi } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { isForbidden } from '../../utils/accessDenied';
import toast from 'react-hot-toast';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDate, isToday, isSunday } from 'date-fns';
import {
  HiOutlineCalendar,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineExclamationCircle,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineUsers,
  HiOutlineDocumentReport,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineClipboardList,
  HiOutlinePencil,
  HiOutlineSave
} from 'react-icons/hi';
import { getCalendarNowIST, formatMonthLongFromIndex0, todayYmdIST, formatCalendarDateDMY } from '../../utils/timezoneDisplay';
import { INLINE_FILTER_BAR, INLINE_FILTER_FIELD } from '../../utils/inlineListFilters';

const MD_BREAKPOINT = 768;

function useIsNarrowScreen() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

function monthDateRange(month, year) {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    date_from: `${year}-${String(month).padStart(2, '0')}-01`,
    date_to: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}

const actionBadgeClass = (action) => {
  if (action === 'created') return 'bg-emerald-100 text-emerald-800';
  if (action === 'updated') return 'bg-amber-100 text-amber-800';
  if (action === 'cleared') return 'bg-red-100 text-red-800';
  if (action === 'auto_absent') return 'bg-violet-100 text-violet-800';
  return 'bg-gray-100 text-gray-700';
};

const formatLogActionLabel = (action) => {
  if (action === 'auto_absent') return 'Auto absent';
  return action;
};

const countMonthAttendanceMarks = (attendanceByDay) =>
  Object.values(attendanceByDay || {}).filter((a) => a?.id).length;

function buildAttendanceTooltip(attendance, { includeUpdater = false } = {}) {
  if (!attendance) return '';
  const parts = [attendance.status];
  if (attendance.check_in_time) {
    parts.push(attendance.check_out_time
      ? `${attendance.check_in_time} – ${attendance.check_out_time}`
      : attendance.check_in_time);
  }
  if (attendance.notes) {
    parts.push(`Note: ${attendance.notes}`);
  }
  if (includeUpdater && attendance.marked_by_name) {
    parts.push(`Updated by ${attendance.marked_by_name}`);
  }
  return parts.join(' · ');
}

const PatientAttendance = () => {
  const { canRead, isTherapist, canDelete, isAdmin } = usePermissions();
  const todayOnlyMode = !isAdmin;
  const isNarrow = useIsNarrowScreen();
  const [register, setRegister] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => getCalendarNowIST().month);
  const [selectedYear, setSelectedYear] = useState(() => getCalendarNowIST().year);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [markingDate, setMarkingDate] = useState(null);
  const [markingRecords, setMarkingRecords] = useState([]);
  const [view, setView] = useState('register'); // 'register' or 'summary'
  const [selectAll, setSelectAll] = useState(false);
  const [markModalMode, setMarkModalMode] = useState('all'); // 'single' | 'all'
  const [markModalPatientId, setMarkModalPatientId] = useState(null);
  const [selectedMobileDay, setSelectedMobileDay] = useState(() => {
    const now = getCalendarNowIST();
    const last = new Date(now.year, now.month, 0).getDate();
    return Math.min(now.day, last);
  });
  const [quickMarkSavingId, setQuickMarkSavingId] = useState(null);
  const [quickNoteSavingId, setQuickNoteSavingId] = useState(null);
  const [quickRegisterNotes, setQuickRegisterNotes] = useState({});
  const [clearingAttendanceId, setClearingAttendanceId] = useState(null);
  const [clearingAllPatientId, setClearingAllPatientId] = useState(null);
  const [markModalSearch, setMarkModalSearch] = useState('');
  const [bulkSaveLoading, setBulkSaveLoading] = useState(false);
  const [markModalScope, setMarkModalScope] = useState('day'); // 'day' | 'month'
  const [monthMarkSkipSundays, setMonthMarkSkipSundays] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPagination, setAuditPagination] = useState({});
  const [logFilters, setLogFilters] = useState({
    search: '',
    action: '',
    performed_from: '',
    performed_to: '',
    page: 1,
  });
  const [appliedLogFilters, setAppliedLogFilters] = useState({
    search: '',
    action: '',
    performed_from: '',
    performed_to: '',
    page: 1,
  });

  const attendanceMonthRange = useMemo(
    () => monthDateRange(selectedMonth, selectedYear),
    [selectedMonth, selectedYear]
  );

  const fetchData = useCallback(async () => {
    if (!canRead('attendance')) {
      setRegister(null);
      setAttendanceReport([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [registerRes, reportRes] = await Promise.all([
        patientAttendanceApi.getDailyRegister({ month: selectedMonth, year: selectedYear }),
        patientAttendanceApi.getAttendanceReport({ month: selectedMonth, year: selectedYear })
      ]);
      if (registerRes.data.success) {
        setRegister(registerRes.data.data);
      }
      if (reportRes.data.success) {
        setAttendanceReport(reportRes.data.data);
      }
    } catch (error) {
      if (isForbidden(error)) {
        setRegister(null);
        setAttendanceReport([]);
      } else {
        console.error('Fetch attendance error:', error);
        toast.error(error.response?.data?.message || 'Failed to fetch attendance data');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, canRead]);

  const fetchAuditLogs = useCallback(async () => {
    if (!isAdmin || !canRead('attendance')) return;
    setAuditLoading(true);
    try {
      const params = {
        ...attendanceMonthRange,
        page: appliedLogFilters.page,
        limit: 50,
        ...(appliedLogFilters.search ? { search: appliedLogFilters.search } : {}),
        ...(appliedLogFilters.action ? { action: appliedLogFilters.action } : {}),
        ...(appliedLogFilters.performed_from ? { performed_from: appliedLogFilters.performed_from } : {}),
        ...(appliedLogFilters.performed_to ? { performed_to: appliedLogFilters.performed_to } : {}),
      };
      const res = await patientAttendanceApi.getAuditLogs(params);
      if (res.data.success) {
        setAuditLogs(res.data.data.logs || []);
        setAuditPagination(res.data.data.pagination || {});
      }
    } catch (error) {
      if (!isForbidden(error)) {
        toast.error(error.response?.data?.message || 'Failed to fetch activity logs');
      }
      setAuditLogs([]);
      setAuditPagination({});
    } finally {
      setAuditLoading(false);
    }
  }, [isAdmin, canRead, attendanceMonthRange, appliedLogFilters]);

  const applyLogFilters = useCallback(() => {
    setAppliedLogFilters({ ...logFilters, page: 1 });
    setLogFilters((prev) => ({ ...prev, page: 1 }));
  }, [logFilters]);

  useEffect(() => {
    if (view === 'logs' && isAdmin) {
      fetchAuditLogs();
    }
  }, [view, isAdmin, fetchAuditLogs]);

  useEffect(() => {
    if (view === 'logs' && isAdmin) {
      setAppliedLogFilters((prev) => ({ ...prev, page: 1 }));
      setLogFilters((prev) => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  const reloadRegisterOnly = useCallback(async () => {
    if (!canRead('attendance')) return;
    try {
      const registerRes = await patientAttendanceApi.getDailyRegister({
        month: selectedMonth,
        year: selectedYear,
      });
      if (registerRes.data.success) {
        setRegister(registerRes.data.data);
      }
    } catch (e) {
      if (!isForbidden(e)) console.error(e);
    }
  }, [selectedMonth, selectedYear, canRead]);

  const reloadAttendanceReportOnly = useCallback(async () => {
    if (!canRead('attendance')) return;
    try {
      const reportRes = await patientAttendanceApi.getAttendanceReport({
        month: selectedMonth,
        year: selectedYear,
      });
      if (reportRes.data.success) {
        setAttendanceReport(reportRes.data.data);
      }
    } catch (e) {
      if (!isForbidden(e)) console.error(e);
    }
  }, [selectedMonth, selectedYear, canRead]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isTherapist) setView('register');
  }, [isTherapist]);

  useEffect(() => {
    if (!todayOnlyMode) return;
    const now = getCalendarNowIST();
    setSelectedMonth(now.month);
    setSelectedYear(now.year);
    setSelectedMobileDay(now.day);
  }, [todayOnlyMode]);

  const isIstTodayInView = useCallback((dayNum) => {
    const now = getCalendarNowIST();
    return (
      selectedYear === now.year &&
      selectedMonth === now.month &&
      parseInt(dayNum, 10) === now.day
    );
  }, [selectedMonth, selectedYear]);

  const parseYmdLocal = (dateLike) => {
    if (!dateLike) return null;
    const ymd = String(dateLike).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [y, m, d] = ymd.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const dt = new Date(dateLike);
    if (Number.isNaN(dt.getTime())) return null;
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  };

  const isBeforeDate = (dayNum, dateLike) => {
    const start = parseYmdLocal(dateLike);
    if (!start) return false;
    const cellDate = new Date(selectedYear, selectedMonth - 1, dayNum);
    return cellDate < start;
  };

  const isBeforeTherapyStart = (item, dayNum) =>
    isBeforeDate(dayNum, item?.patient?.therapy_start_date_min);

  const isBeforeEnrollment = (item, dayNum) =>
    isBeforeDate(dayNum, item?.patient?.enrollment_date);

  const ymdForDay = (dayNum) =>
    `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;

  const isInInactiveGap = (item, dayNum) => {
    const periods = item?.patient?.status_periods;
    if (!Array.isArray(periods) || periods.length === 0) return false;
    const dateStr = ymdForDay(dayNum);
    return periods.some((p) => {
      if (p.status === 'active' || !p.started_on) return false;
      if (dateStr < p.started_on) return false;
      if (p.ended_on && dateStr >= p.ended_on) return false;
      return true;
    });
  };

  const isAttendanceLocked = (item, dayNum) =>
    isBeforeTherapyStart(item, dayNum) ||
    isBeforeEnrollment(item, dayNum) ||
    isInInactiveGap(item, dayNum);

  const lockedAttendanceTitle = (item, dayNum) => {
    if (isBeforeTherapyStart(item, dayNum) || isBeforeEnrollment(item, dayNum)) {
      const enroll = item?.patient?.enrollment_date;
      const therapy = item?.patient?.therapy_start_date_min;
      const from = [enroll, therapy].filter(Boolean).sort().pop();
      return `Attendance starts from ${formatCalendarDateDMY(from)}`;
    }
    return 'Patient was inactive';
  };

  const buildMarkingRecords = useCallback((dateNum, onlyPatientId = null) => {
    if (!register) return [];
    const day = parseInt(dateNum, 10);
    if (!day || Number.isNaN(day)) return [];

    const base = Array.isArray(register.register) ? register.register : [];
    const subset = onlyPatientId ? base.filter((it) => it?.patient?.id === onlyPatientId) : base;

    return subset
      .filter((item) => !isAttendanceLocked(item, day))
      .map((item) => {
        const dayAttendance = item.attendance[day];
        return {
          patient_id: item.patient.id,
          patient_name: item.patient.full_name,
          patient_code: item.patient.patient_code,
          attendance_id: dayAttendance?.id || null,
          status: dayAttendance?.status || 'absent',
          original_status: dayAttendance?.status || null,
          has_existing_attendance: !!dayAttendance,
          check_in_time: dayAttendance?.check_in_time || '',
          check_out_time: dayAttendance?.check_out_time || '',
          notes: dayAttendance?.notes || '',
          marked_by_name: dayAttendance?.marked_by_name || null,
        };
      });
  }, [register]);

  const openMarkModal = useCallback((dateNum, patientId = null) => {
    if (todayOnlyMode && !isIstTodayInView(dateNum)) {
      toast.error('You can only mark attendance for today');
      return;
    }
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(dateNum).padStart(2, '0')}`;
    setMarkingDate(dateStr);

    const pid = patientId ? Number(patientId) : null;
    const mode = pid ? 'single' : 'all';
    setMarkModalMode(mode);
    setMarkModalPatientId(pid);

    const records = buildMarkingRecords(dateNum, mode === 'single' ? pid : null);
      if (records.length === 0) {
        toast('No patients are eligible for attendance on this date.', { icon: 'ℹ️' });
        return;
      }
      setMarkingRecords(records);
      setSelectAll(false);
    setMarkModalSearch('');
    setMarkModalScope('day');
    setMonthMarkSkipSundays(true);
      setShowMarkModal(true);
  }, [buildMarkingRecords, selectedMonth, selectedYear, todayOnlyMode, isIstTodayInView]);

  const handleDateClick = (date, patientId = null) => {
    if (!register) return;
    openMarkModal(date, patientId);
  };

  const getPatientRecordsToSave = () => {
    const list =
      markModalMode === 'single' && markModalPatientId
        ? markingRecords.filter((r) => r.patient_id === markModalPatientId)
        : markingRecords;
    return list.map((record) => ({
      patient_id: record.patient_id,
      status: record.status,
      check_in_time:
        record.check_in_time && record.check_in_time.trim() !== '' ? record.check_in_time : null,
      check_out_time:
        record.check_out_time && record.check_out_time.trim() !== '' ? record.check_out_time : null,
      notes: record.notes && record.notes.trim() !== '' ? record.notes : null,
    }));
  };

  const handleBulkMark = async () => {
    try {
      const normalizedRecords = getPatientRecordsToSave();
      if (normalizedRecords.length === 0) {
        toast.error('No patients selected to save');
        return;
      }

      if (markModalScope === 'month') {
        if (!isAdmin) {
          toast.error('Full month marking is only available for administrators');
          return;
        }
        const monthLabel = format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy');
        const skipNote = monthMarkSkipSundays ? ' (Sundays skipped)' : '';
        const shouldProceed = window.confirm(
          `Apply "${normalizedRecords[0]?.status}" for ${normalizedRecords.length} patient(s) for all days in ${monthLabel}${skipNote}?\n\nExisting marks on those days will be overwritten.`
        );
        if (!shouldProceed) return;

        setBulkSaveLoading(true);
        await patientAttendanceApi.bulkMarkAttendanceMonth({
          month: selectedMonth,
          year: selectedYear,
          skip_sundays: monthMarkSkipSundays,
          records: normalizedRecords,
        });
        toast.success('Full month attendance saved');
      } else {
        const changedExisting = markingRecords.filter(
          (record) =>
            normalizedRecords.some((n) => n.patient_id === record.patient_id) &&
            record.has_existing_attendance &&
            record.original_status !== record.status
        );

        if (changedExisting.length > 0) {
          const sample = changedExisting
            .slice(0, 3)
            .map((record) => `${record.patient_name}: ${record.original_status} -> ${record.status}`)
            .join('\n');
          const extra = changedExisting.length > 3 ? `\n...and ${changedExisting.length - 3} more` : '';
          const shouldProceed = window.confirm(
            `Some patients already have attendance for this date.\n\n${sample}${extra}\n\nDo you want to update existing attendance?`
          );
          if (!shouldProceed) return;
        }

        setBulkSaveLoading(true);
      await patientAttendanceApi.bulkMarkAttendance({
        attendance_date: markingDate,
          records: normalizedRecords,
      });
      toast.success('Attendance marked successfully');
      }

      setShowMarkModal(false);
      setMarkModalSearch('');
      const refreshes = [reloadRegisterOnly()];
      if (!isTherapist) {
        refreshes.push(reloadAttendanceReportOnly());
      }
      await Promise.all(refreshes);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setBulkSaveLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'absent': return 'bg-rose-100 text-rose-700 border-rose-300';
      case 'late': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'excused': return 'bg-sky-100 text-sky-700 border-sky-300';
      case 'holiday': return 'bg-violet-100 text-violet-700 border-violet-300';
      default: return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'P';
      case 'absent': return 'A';
      case 'late': return 'L';
      case 'excused': return 'E';
      case 'holiday': return 'H';
      default: return '-';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <HiOutlineCheckCircle className="w-4 h-4" />;
      case 'absent': return <HiOutlineXCircle className="w-4 h-4" />;
      case 'late': return <HiOutlineClock className="w-4 h-4" />;
      case 'excused': return <HiOutlineExclamationCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const monthStart = startOfMonth(new Date(selectedYear, selectedMonth - 1));
  const monthEnd = endOfMonth(new Date(selectedYear, selectedMonth - 1));
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const registerDays = useMemo(() => {
    if (!todayOnlyMode) return daysInMonth;
    const now = getCalendarNowIST();
    return daysInMonth.filter(
      (day) =>
        getDate(day) === now.day &&
        selectedMonth === now.month &&
        selectedYear === now.year
    );
  }, [todayOnlyMode, daysInMonth, selectedMonth, selectedYear]);

  const todayDayDate = useMemo(() => registerDays[0] || null, [registerDays]);

  const singleDayMobileRegister =
    (isTherapist || todayOnlyMode) && isNarrow && view === 'register';

  useEffect(() => {
    if (!singleDayMobileRegister) return;
    const now = getCalendarNowIST();
    const last = daysInMonth.length;
    setSelectedMobileDay((prev) => {
      if (todayOnlyMode || (selectedYear === now.year && selectedMonth === now.month)) {
        return Math.min(now.day, last);
      }
      return Math.min(Math.max(prev, 1), last);
    });
  }, [selectedMonth, selectedYear, singleDayMobileRegister, daysInMonth.length, todayOnlyMode]);

  const effectiveMobileDay = todayOnlyMode
    ? getCalendarNowIST().day
    : selectedMobileDay;

  const selectedMobileDayDate = useMemo(() => {
    if (todayOnlyMode && todayDayDate) return todayDayDate;
    const idx = Math.min(Math.max(selectedMobileDay, 1), daysInMonth.length) - 1;
    return daysInMonth[idx] || daysInMonth[0];
  }, [daysInMonth, selectedMobileDay, todayOnlyMode, todayDayDate]);

  useEffect(() => {
    if (!singleDayMobileRegister || !register?.register) return;
    const day = effectiveMobileDay;
    const next = {};
    for (const item of register.register) {
      const att = item.attendance[day];
      next[item.patient.id] = att?.notes || '';
    }
    setQuickRegisterNotes(next);
  }, [register, effectiveMobileDay, singleDayMobileRegister]);

  const selectedMobileDateStr = useMemo(() => {
    if (todayOnlyMode) return todayYmdIST();
    return `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(effectiveMobileDay).padStart(2, '0')}`;
  }, [selectedYear, selectedMonth, effectiveMobileDay, todayOnlyMode]);

  const filteredMarkingRecords = useMemo(() => {
    const q = markModalSearch.trim().toLowerCase();
    if (!q) return markingRecords;
    return markingRecords.filter(
      (r) =>
        (r.patient_name && r.patient_name.toLowerCase().includes(q)) ||
        (r.patient_code && String(r.patient_code).toLowerCase().includes(q))
    );
  }, [markingRecords, markModalSearch]);

  const handleMarkAllPresent = () => {
    const limitIds = markModalSearch.trim()
      ? new Set(filteredMarkingRecords.map((r) => r.patient_id))
      : null;
    const updated = markingRecords.map((record) => {
      if (limitIds && !limitIds.has(record.patient_id)) return record;
      return {
        ...record,
        status:
          record.has_existing_attendance && record.original_status === 'absent'
            ? 'absent'
            : 'present',
      };
    });
    setMarkingRecords(updated);
    const visible = limitIds ? updated.filter((r) => limitIds.has(r.patient_id)) : updated;
    setSelectAll(visible.length > 0 && visible.every((record) => record.status === 'present'));
  };

  const handleMarkAllAbsent = () => {
    const limitIds = markModalSearch.trim()
      ? new Set(filteredMarkingRecords.map((r) => r.patient_id))
      : null;
    const updated = markingRecords.map((record) => {
      if (limitIds && !limitIds.has(record.patient_id)) return record;
      return { ...record, status: 'absent' };
    });
    setMarkingRecords(updated);
    setSelectAll(false);
  };

  const getQuickNoteForPatient = (patientId, att) => {
    if (Object.prototype.hasOwnProperty.call(quickRegisterNotes, patientId)) {
      return quickRegisterNotes[patientId];
    }
    return att?.notes || '';
  };

  const normalizeQuickNote = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed === '' ? null : trimmed;
  };

  const handleQuickMarkOne = async (patientId, status, att = null) => {
    try {
      setQuickMarkSavingId(patientId);
      const noteText = getQuickNoteForPatient(patientId, att);
      await patientAttendanceApi.markAttendance({
        patient_id: patientId,
        attendance_date: selectedMobileDateStr,
        status,
        check_in_time: att?.check_in_time || null,
        check_out_time: att?.check_out_time || null,
        notes: normalizeQuickNote(noteText),
      });
      await reloadRegisterOnly();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save attendance');
    } finally {
      setQuickMarkSavingId(null);
    }
  };

  const isQuickNoteDirty = (patientId, att) => {
    const draft = normalizeQuickNote(getQuickNoteForPatient(patientId, att));
    const saved = normalizeQuickNote(att?.notes);
    return draft !== saved;
  };

  const handleQuickNoteSave = async (patientId, status, att) => {
    if (!status || !att?.id) {
      toast.error('Mark attendance (P / A / L / E / H) first, then save the note');
      return;
    }
    if (!isQuickNoteDirty(patientId, att)) {
      toast('No changes to save', { icon: 'ℹ️' });
      return;
    }
    const nextNote = normalizeQuickNote(getQuickNoteForPatient(patientId, att));
    try {
      setQuickNoteSavingId(patientId);
      await patientAttendanceApi.markAttendance({
        patient_id: patientId,
        attendance_date: selectedMobileDateStr,
        status,
        check_in_time: att.check_in_time || null,
        check_out_time: att.check_out_time || null,
        notes: nextNote,
      });
      await reloadRegisterOnly();
      toast.success('Note saved');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save note');
    } finally {
      setQuickNoteSavingId(null);
    }
  };

  const handleClearAttendance = async (attendanceId, patientName) => {
    if (!canDelete('attendance')) {
      toast.error('You do not have permission to clear attendance');
      return;
    }
    if (!attendanceId) return;
    const label = patientName ? ` for ${patientName}` : '';
    if (!window.confirm(`Remove attendance${label} on this date? The cell will be empty until you mark again.`)) {
      return;
    }
    try {
      setClearingAttendanceId(attendanceId);
      await patientAttendanceApi.deleteAttendance(attendanceId);
      toast.success('Attendance cleared');
      await reloadRegisterOnly();
      setMarkingRecords((prev) =>
        prev.map((r) =>
          r.attendance_id === attendanceId
            ? {
                ...r,
                attendance_id: null,
                has_existing_attendance: false,
                original_status: null,
                status: 'absent',
                check_in_time: '',
                check_out_time: '',
                notes: '',
              }
            : r
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not clear attendance');
    } finally {
      setClearingAttendanceId(null);
    }
  };

  const handleClearAllMonthAttendance = async (patientId, patientName, attendanceByDay) => {
    if (!isAdmin) {
      toast.error('Only administrators can clear a full month of attendance');
      return;
    }
    const markedCount = countMonthAttendanceMarks(attendanceByDay);
    if (markedCount === 0) {
      toast.error('No attendance marks to clear for this month');
      return;
    }
    const monthLabel = `${formatMonthLongFromIndex0(selectedMonth - 1)} ${selectedYear}`;
    if (
      !window.confirm(
        `Clear all ${markedCount} attendance mark(s) for ${patientName} in ${monthLabel}? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setClearingAllPatientId(patientId);
      const res = await patientAttendanceApi.clearMonthAttendance({
        patient_id: patientId,
        month: selectedMonth,
        year: selectedYear,
      });
      toast.success(res.data?.message || 'Month attendance cleared');
      await reloadRegisterOnly();
      if (view === 'summary') {
        await reloadAttendanceReportOnly();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not clear month attendance');
    } finally {
      setClearingAllPatientId(null);
    }
  };

  // Calculate totals from report
  const totals = attendanceReport.reduce((acc, report) => ({
    present: acc.present + (parseInt(report.present_days) || 0),
    absent: acc.absent + (parseInt(report.absent_days) || 0),
    late: acc.late + (parseInt(report.late_days) || 0),
    excused: acc.excused + (parseInt(report.excused_days) || 0),
  }), { present: 0, absent: 0, late: 0, excused: 0 });

  // Helper function to calculate working days (excluding Sundays) in a date range
  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0) { // Not Sunday
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  // Helper function to calculate charges and remaining days for a patient
  const calculatePatientCharges = (report) => {
    if (!report.therapy_plan_id || !report.fee_per_session) {
      return null;
    }

    const currentMonthStart = new Date(selectedYear, selectedMonth - 1, 1);
    const currentMonthEnd = new Date(selectedYear, selectedMonth, 0);
    const nextMonthStart = new Date(selectedYear, selectedMonth, 1);
    const nextMonthEnd = new Date(selectedYear, selectedMonth + 1, 0);

    const therapyStart = report.therapy_start_date ? new Date(report.therapy_start_date) : null;
    const therapyEnd = report.therapy_end_date ? new Date(report.therapy_end_date) : null;

    // Calculate effective start and end dates for current month
    const effectiveStart = therapyStart && therapyStart > currentMonthStart ? therapyStart : currentMonthStart;
    const effectiveEnd = therapyEnd && therapyEnd < currentMonthEnd ? therapyEnd : currentMonthEnd;

    // Calculate working days in current month (excluding Sundays) within therapy plan period
    const workingDaysCurrentMonth = calculateWorkingDays(effectiveStart, effectiveEnd);

    // Present days (including Sundays if present)
    const presentDays = parseInt(report.present_days) || 0;
    const presentSundays = parseInt(report.present_sundays) || 0;

    // Calculate charges
    const chargesForPresentDays = presentDays * (parseFloat(report.fee_per_session) || 0);

    // Calculate remaining days in current month
    const remainingDaysCurrentMonth = Math.max(0, workingDaysCurrentMonth - presentDays);

    // Calculate remaining days in next month
    let remainingDaysNextMonth = 0;
    if (therapyEnd && therapyEnd > currentMonthEnd) {
      const nextMonthEffectiveStart = nextMonthStart > therapyStart ? nextMonthStart : therapyStart;
      const nextMonthEffectiveEnd = therapyEnd < nextMonthEnd ? therapyEnd : nextMonthEnd;
      if (nextMonthEffectiveStart <= nextMonthEffectiveEnd) {
        remainingDaysNextMonth = calculateWorkingDays(nextMonthEffectiveStart, nextMonthEffectiveEnd);
      }
    }

    return {
      workingDaysCurrentMonth,
      presentDays,
      presentSundays,
      chargesForPresentDays,
      remainingDaysCurrentMonth,
      remainingDaysNextMonth,
      feePerSession: parseFloat(report.fee_per_session) || 0,
      totalExpectedCharges: workingDaysCurrentMonth * (parseFloat(report.fee_per_session) || 0)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Patient Attendance</h1>
          <p className="text-gray-500 mt-1">
            {todayOnlyMode
              ? 'Mark attendance for today only'
              : isTherapist
                ? 'Mark daily attendance for your assigned patients — tap Save to keep note changes'
                : 'Track and manage daily patient attendance'}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* View toggle: therapists only use the daily register (no summary / logs). */}
          {!isTherapist && (
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
                type="button"
              onClick={() => setView('register')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                view === 'register' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
                <HiOutlineCalendar className="mr-1.5 inline h-4 w-4" />
              Register
            </button>
            <button
                type="button"
              onClick={() => setView('summary')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                view === 'summary' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
                <HiOutlineDocumentReport className="mr-1.5 inline h-4 w-4" />
              Summary
            </button>
            {isAdmin && !todayOnlyMode ? (
              <button
                type="button"
                onClick={() => setView('logs')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                  view === 'logs'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <HiOutlineClipboardList className="mr-1.5 inline h-4 w-4" />
                Activity Log
              </button>
            ) : null}
          </div>
          )}

          {todayOnlyMode ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800">
              <HiOutlineCalendar className="h-5 w-5 text-primary-600" />
              {selectedMobileDayDate
                ? format(selectedMobileDayDate, 'EEEE, MMMM d, yyyy')
                : 'Today'}
            </div>
          ) : (
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2 py-1">
            <button 
              onClick={handlePrevMonth}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            >
              <HiOutlineChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-2 py-1 border-0 bg-transparent text-sm font-medium focus:ring-0"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {formatMonthLongFromIndex0(i)}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-2 py-1 border-0 bg-transparent text-sm font-medium focus:ring-0"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
            <button 
              onClick={handleNextMonth}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            >
              <HiOutlineChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Total Present</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{totals.present}</p>
            </div>
            <div className="p-3 bg-emerald-200 rounded-xl">
              <HiOutlineCheckCircle className="w-6 h-6 text-emerald-700" />
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-rose-600 uppercase tracking-wide">Total Absent</p>
              <p className="text-2xl font-bold text-rose-700 mt-1">{totals.absent}</p>
            </div>
            <div className="p-3 bg-rose-200 rounded-xl">
              <HiOutlineXCircle className="w-6 h-6 text-rose-700" />
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Total Late</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">{totals.late}</p>
            </div>
            <div className="p-3 bg-amber-200 rounded-xl">
              <HiOutlineClock className="w-6 h-6 text-amber-700" />
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-sky-600 uppercase tracking-wide">Total Excused</p>
              <p className="text-2xl font-bold text-sky-700 mt-1">{totals.excused}</p>
            </div>
            <div className="p-3 bg-sky-200 rounded-xl">
              <HiOutlineExclamationCircle className="w-6 h-6 text-sky-700" />
            </div>
          </div>
        </div>
      </div> */}

      {view === 'logs' && isAdmin ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Who created, updated, or cleared attendance for each patient. Attendance dates use the month selected above.
          </p>
          <div className={INLINE_FILTER_BAR}>
            <div className="relative min-w-[9rem] flex-1 max-w-[14rem]">
              <HiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={logFilters.search}
                onChange={(e) => setLogFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
                placeholder="Patient name or code…"
                className={`${INLINE_FILTER_FIELD} w-full pl-8`}
              />
            </div>
            <select
              value={logFilters.action}
              onChange={(e) => setLogFilters((prev) => ({ ...prev, action: e.target.value, page: 1 }))}
              className={`${INLINE_FILTER_FIELD} w-auto min-w-[7rem]`}
              aria-label="Action type"
            >
              <option value="">All actions</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
              <option value="cleared">Cleared</option>
              <option value="auto_absent">Auto absent</option>
            </select>
            <input
              type="date"
              value={logFilters.performed_from}
              onChange={(e) => setLogFilters((prev) => ({ ...prev, performed_from: e.target.value, page: 1 }))}
              className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
              aria-label="Logged from"
              title="Logged from (when action happened)"
            />
            <span className="text-xs text-gray-400">–</span>
            <input
              type="date"
              value={logFilters.performed_to}
              onChange={(e) => setLogFilters((prev) => ({ ...prev, performed_to: e.target.value, page: 1 }))}
              className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
              min={logFilters.performed_from || undefined}
              aria-label="Logged to"
              title="Logged to (when action happened)"
            />
            <button
              type="button"
              onClick={applyLogFilters}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
            >
              Apply
            </button>
          </div>

          <div className="card overflow-hidden p-0">
            {auditLoading ? (
              <div className="flex h-48 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <HiOutlineClipboardList className="mx-auto mb-2 h-12 w-12 text-gray-300" />
                <p>No activity logs for this period</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px]">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      <th className="table-header">Attendance date</th>
                      <th className="table-header">Logged at</th>
                      <th className="table-header">Patient</th>
                      <th className="table-header">Action</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">By</th>
                      <th className="table-header">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 text-sm">
                        <td className="table-cell whitespace-nowrap">
                          {formatCalendarDateDMY(log.attendance_date)}
                        </td>
                        <td className="table-cell whitespace-nowrap text-gray-600">{log.performed_at}</td>
                        <td className="table-cell">
                          <p className="font-medium text-gray-900">{log.patient_name}</p>
                          <p className="text-xs text-gray-500">{log.patient_code}</p>
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${actionBadgeClass(log.action)}`}>
                            {formatLogActionLabel(log.action)}
                          </span>
                        </td>
                        <td className="table-cell capitalize text-gray-700">
                          {log.action === 'cleared' ? (
                            <span className="text-red-700">{log.status_before || '—'} → cleared</span>
                          ) : log.status_before && log.status_before !== log.status_after ? (
                            <span>{log.status_before} → {log.status_after}</span>
                          ) : (
                            <span>{log.status_after || '—'}</span>
                          )}
                        </td>
                        <td className="table-cell text-gray-700">
                          {log.action === 'auto_absent' && !log.performed_by_name
                            ? 'System'
                            : log.performed_by_name || '—'}
                        </td>
                        <td className="table-cell max-w-[200px] truncate text-gray-500" title={log.notes || ''}>
                          {log.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {auditPagination.totalPages > 1 ? (
              <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
                <p className="text-sm text-gray-500">
                  Page {auditPagination.page} of {auditPagination.totalPages} ({auditPagination.total} entries)
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={auditPagination.page <= 1}
                    onClick={() => setAppliedLogFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                    className="btn btn-secondary text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={auditPagination.page >= auditPagination.totalPages}
                    onClick={() => setAppliedLogFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                    className="btn btn-secondary text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : view === 'register' ? (
        /* Daily Register — therapist mobile: day strip + quick buttons */
        singleDayMobileRegister ? (
          <div className="card overflow-hidden p-0">
            <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <p className="text-xs font-medium text-gray-500">
                  {todayOnlyMode ? 'Today' : 'Select day'}
                </p>
                <button
                  type="button"
                  onClick={() => handleDateClick(effectiveMobileDay, null)}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  Bulk form →
                </button>
              </div>
              {!todayOnlyMode ? (
              <div className="flex gap-1 overflow-x-auto px-2 pb-2 pt-0 [-webkit-overflow-scrolling:touch]">
                {daysInMonth.map((day) => {
                  const dayNum = getDate(day);
                  const isTodayDate = isToday(day);
                  const isSundayDate = isSunday(day);
                  const selected = selectedMobileDay === dayNum;
                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => setSelectedMobileDay(dayNum)}
                      className={`flex min-w-[3.25rem] shrink-0 flex-col items-center rounded-xl border px-2 py-1.5 text-center transition-all ${
                        selected
                          ? 'border-primary-500 bg-primary-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      } ${isTodayDate ? 'ring-2 ring-primary-200 ring-offset-1' : ''} ${isSundayDate ? 'bg-red-50/80' : ''}`}
                    >
                      <span className={`text-[10px] font-medium uppercase ${isSundayDate ? 'text-red-600' : 'text-gray-500'}`}>
                        {format(day, 'EEE')}
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          selected ? 'text-primary-700' : isSundayDate ? 'text-red-700' : 'text-gray-900'
                        }`}
                      >
                        {dayNum}
                      </span>
                    </button>
                  );
                })}
              </div>
              ) : null}
              <div className="grid grid-cols-5 gap-1 border-t border-gray-100 bg-gray-50/90 px-2 py-2 text-[10px]">
                <div className="flex flex-col items-center gap-0.5 text-center">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-100 text-[10px] font-bold text-emerald-800">
                    P
                  </span>
                  <span className="text-gray-600">Present</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 text-center">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-rose-100 text-[10px] font-bold text-rose-800">
                    A
                  </span>
                  <span className="text-gray-600">Absent</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 text-center">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-100 text-[10px] font-bold text-amber-800">
                    L
                  </span>
                  <span className="text-gray-600">Late</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 text-center">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-sky-100 text-[10px] font-bold text-sky-800">
                    E
                  </span>
                  <span className="text-gray-600">Excused</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 text-center">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-violet-100 text-[10px] font-bold text-violet-800">
                    H
                  </span>
                  <span className="text-gray-600">Holiday</span>
                </div>
              </div>
            </div>

            <div className="border-b border-gray-100 bg-primary-50/40 px-3 py-2">
              <p className="text-center text-sm font-semibold text-gray-900">
                {selectedMobileDayDate ? format(selectedMobileDayDate, 'EEEE, MMM d') : ''}
              </p>
              <p className="text-center text-xs text-gray-500">
                Tap a letter for attendance · use Save to keep note changes
              </p>
            </div>

            <div className="max-h-[min(60vh,calc(100dvh-22rem))] space-y-3 overflow-y-auto p-3">
              {register?.register.length === 0 ? (
                <div className="py-10 text-center text-gray-500">
                  <HiOutlineUsers className="mx-auto mb-2 h-10 w-10 text-gray-300" />
                  <p>No active patients</p>
                </div>
              ) : (
                register.register.map((item) => {
                  const locked = isAttendanceLocked(item, effectiveMobileDay);
                  const att = item.attendance[effectiveMobileDay];
                  const current = att?.status || null;
                  const busy =
                    quickMarkSavingId === item.patient.id || quickNoteSavingId === item.patient.id;
                  const noteDirty = isQuickNoteDirty(item.patient.id, att);
                  const noteSaving = quickNoteSavingId === item.patient.id;
                  const statuses = [
                    { key: 'present', label: 'P', title: 'Present', active: 'bg-emerald-500 text-white border-emerald-600' },
                    { key: 'absent', label: 'A', title: 'Absent', active: 'bg-rose-500 text-white border-rose-600' },
                    { key: 'late', label: 'L', title: 'Late', active: 'bg-amber-500 text-white border-amber-600' },
                    { key: 'excused', label: 'E', title: 'Excused', active: 'bg-sky-500 text-white border-sky-600' },
                    { key: 'holiday', label: 'H', title: 'Holiday', active: 'bg-violet-500 text-white border-violet-600' },
                  ];
                  return (
                    <div
                      key={item.patient.id}
                      className={`rounded-2xl border p-3 shadow-sm ${
                        locked ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="relative pr-9">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-gray-900" title={item.patient.full_name}>
                            {item.patient.full_name}
                          </p>
                          <p className="truncate text-xs text-primary-600">{item.patient.patient_code}</p>
                          {item.patient.therapy_start_date_min && (
                            <p className="mt-0.5 text-[11px] text-gray-400">
                              Start {formatCalendarDateDMY(item.patient.therapy_start_date_min)}
                            </p>
                          )}
                        </div>
                        {isAdmin && countMonthAttendanceMarks(item.attendance) > 0 ? (
                          <button
                            type="button"
                            title="Clear all attendance this month"
                            disabled={clearingAllPatientId === item.patient.id}
                            onClick={() =>
                              handleClearAllMonthAttendance(
                                item.patient.id,
                                item.patient.full_name,
                                item.attendance
                              )
                            }
                            className="absolute right-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                          >
                            {clearingAllPatientId === item.patient.id ? (
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-rose-500 border-t-transparent" />
                            ) : (
                              <HiOutlineTrash className="h-4 w-4" />
                            )}
                          </button>
                        ) : null}
                        <div className="mt-2 flex items-center gap-2">
                          {busy ? (
                            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
                          ) : null}
                          {current && !locked ? (
                            <span
                              className={`rounded-lg border px-2 py-0.5 text-xs font-bold ${getStatusColor(current)}`}
                            >
                              {getStatusLabel(current)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {isAdmin && att?.marked_by_name && !locked ? (
                        <p className="mt-1 text-[10px] text-gray-500 truncate" title={`Updated by ${att.marked_by_name}`}>
                          By {att.marked_by_name}
                        </p>
                      ) : null}
                      {locked ? (
                        <p className="mt-2 text-center text-xs text-gray-500">
                          {lockedAttendanceTitle(item, effectiveMobileDay)}
                        </p>
                      ) : (
                        <>
                          <div className="mt-3 grid grid-cols-5 gap-1.5">
                            {statuses.map((s) => {
                              const isSel = current === s.key;
                              return (
                                <button
                                  key={s.key}
                                  type="button"
                                  title={s.title}
                                  disabled={busy || clearingAttendanceId === att?.id}
                                  onClick={() => handleQuickMarkOne(item.patient.id, s.key, att)}
                                  className={`flex h-12 items-center justify-center rounded-xl border-2 text-base font-bold transition-transform active:scale-95 disabled:opacity-40 ${
                                    isSel ? `${s.active} scale-[1.02]` : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                                  }`}
                                >
                                  {s.label}
                                </button>
                              );
                            })}
                          </div>
                          <div className="mt-3 space-y-1.5">
                            <label
                              htmlFor={`att-note-${item.patient.id}`}
                              className="flex items-center gap-1 text-[11px] font-medium text-gray-600"
                            >
                              <HiOutlinePencil className="h-3.5 w-3.5 text-gray-400" />
                              Note (optional)
                            </label>
                            <div className="flex gap-2">
                              <input
                                id={`att-note-${item.patient.id}`}
                                type="text"
                                value={getQuickNoteForPatient(item.patient.id, att)}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setQuickRegisterNotes((prev) => ({ ...prev, [item.patient.id]: v }));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && current && att?.id && !busy) {
                                    e.preventDefault();
                                    handleQuickNoteSave(item.patient.id, current, att);
                                  }
                                }}
                                disabled={busy || clearingAttendanceId === att?.id}
                                placeholder={
                                  current
                                    ? 'e.g. late arrival, parent called'
                                    : 'Mark attendance first (P / A / L / E / H)'
                                }
                                className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:bg-white focus:ring-2 focus:ring-primary-500/30 disabled:opacity-50"
                              />
                              <button
                                type="button"
                                onClick={() => handleQuickNoteSave(item.patient.id, current, att)}
                                disabled={
                                  !current ||
                                  !att?.id ||
                                  busy ||
                                  clearingAttendanceId === att?.id ||
                                  !noteDirty
                                }
                                className="inline-flex shrink-0 items-center justify-center gap-1 rounded-xl bg-primary-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {noteSaving ? (
                                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                ) : (
                                  <HiOutlineSave className="h-4 w-4" />
                                )}
                                <span>{noteSaving ? '…' : 'Save'}</span>
                              </button>
                            </div>
                            {noteDirty && current ? (
                              <p className="text-[10px] font-medium text-amber-700">
                                Unsaved changes — tap Save to keep this note
                              </p>
                            ) : att?.notes && current ? (
                              <p className="truncate text-[10px] text-gray-500" title={att.notes}>
                                Saved: {att.notes}
                              </p>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => openMarkModal(effectiveMobileDay, item.patient.id)}
                              className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                            >
                              Times &amp; more →
                            </button>
                          </div>
                          {current && att?.id && canDelete('attendance') ? (
                            <button
                              type="button"
                              disabled={clearingAttendanceId === att.id}
                              onClick={() => handleClearAttendance(att.id, item.patient.full_name)}
                              className="mt-2 flex w-full min-h-[40px] items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50/80 py-2 text-xs font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-50"
                            >
                              {clearingAttendanceId === att.id ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-rose-600 border-t-transparent" />
                              ) : (
                                <HiOutlineTrash className="h-4 w-4" />
                              )}
                              Clear attendance
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
        <div className="card overflow-hidden p-0">
          <div className="max-h-[min(70vh,calc(100dvh-16rem))] overflow-auto">
          <div className="min-w-full">
            {register?.register.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <HiOutlineUsers className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p>No active patients found</p>
              </div>
            ) : (
              <table className={`w-full border-separate border-spacing-0 ${todayOnlyMode ? 'min-w-[420px]' : 'min-w-[980px]'}`}>
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="sticky left-0 top-0 z-50 w-[220px] min-w-[220px] max-w-[220px] border-b border-r border-gray-200 bg-white p-3 text-left font-semibold text-gray-700 shadow-[4px_0_8px_-2px_rgba(15,23,42,0.12)]">
                      Patient Name
                    </th>
                    {registerDays.map((day) => {
                      const isTodayDate = isToday(day);
                      const isSundayDate = isSunday(day);
                      return (
                        <th
                          key={day.toISOString()}
                          className={`sticky top-0 z-40 min-w-[50px] cursor-pointer border-b border-gray-200 p-2 text-center font-semibold transition-colors hover:bg-gray-50 ${
                            isTodayDate ? 'bg-primary-50' : isSundayDate ? 'bg-red-50' : 'bg-white'
                          }`}
                          onClick={() => handleDateClick(getDate(day), null)}
                          title={`Click to mark attendance for ${format(day, 'MMM dd')}`}
                        >
                          <div className={`text-xs ${isSundayDate ? 'text-red-500' : 'text-gray-500'}`}>
                            {format(day, 'EEE')}
                          </div>
                          <div className={`text-sm ${isTodayDate ? 'text-primary-600 font-bold' : isSundayDate ? 'text-red-600' : 'text-gray-700'}`}>
                            {getDate(day)}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {register?.register.map((item) => (
                    <tr key={item.patient.id} className="group border-b border-gray-100 hover:bg-gray-50">
                      <td className="sticky left-0 z-30 w-[220px] min-w-[220px] max-w-[220px] border-b border-r border-gray-100 bg-white p-3 shadow-[4px_0_8px_-2px_rgba(15,23,42,0.12)] group-hover:bg-gray-50">
                        <div className="relative pr-8">
                          <div className="min-w-0">
                            <div
                              className="font-medium text-gray-900 truncate"
                              title={item.patient.full_name}
                            >
                              {item.patient.full_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{item.patient.patient_code}</div>
                            {item.patient.therapy_start_date_min && (
                              <div className="text-[11px] text-gray-400 mt-0.5">
                                Start: {formatCalendarDateDMY(item.patient.therapy_start_date_min)}
                              </div>
                            )}
                          </div>
                          {isAdmin && countMonthAttendanceMarks(item.attendance) > 0 ? (
                            <button
                              type="button"
                              title="Clear all attendance this month"
                              disabled={clearingAllPatientId === item.patient.id}
                              onClick={() =>
                                handleClearAllMonthAttendance(
                                  item.patient.id,
                                  item.patient.full_name,
                                  item.attendance
                                )
                              }
                              className="absolute right-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                            >
                              {clearingAllPatientId === item.patient.id ? (
                                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-rose-500 border-t-transparent" />
                              ) : (
                                <HiOutlineTrash className="h-4 w-4" />
                              )}
                            </button>
                          ) : null}
                        </div>
                      </td>
                      {registerDays.map((day) => {
                        const dayNum = getDate(day);
                        const attendance = item.attendance[dayNum];
                        const locked = isAttendanceLocked(item, dayNum);
                        const isSundayDate = isSunday(day);
                        return (
                          <td
                            key={day.toISOString()}
                            className={`text-center p-1 ${isSundayDate ? 'bg-red-50/50' : ''}`}
                            onClick={() => {
                              if (!locked) handleDateClick(dayNum, item.patient.id);
                            }}
                          >
                            {locked ? (
                              <div
                                className="inline-flex items-center justify-center w-9 h-9 cursor-not-allowed select-none"
                                title={lockedAttendanceTitle(item, dayNum)}
                              >
                                <span className="block w-5 h-0.5 bg-red-500 rounded-full" />
                              </div>
                            ) : attendance ? (
                              <div className="relative inline-flex flex-col items-center justify-center gap-0.5 max-w-[56px]">
                                {canDelete('attendance') && attendance.id ? (
                                  <button
                                    type="button"
                                    title="Clear attendance"
                                    disabled={clearingAttendanceId === attendance.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearAttendance(attendance.id, item.patient.full_name);
                                    }}
                                    className="absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                                  >
                                    {clearingAttendanceId === attendance.id ? (
                                      <span className="h-2.5 w-2.5 animate-spin rounded-full border border-rose-500 border-t-transparent" />
                                    ) : (
                                      <HiOutlineTrash className="h-3 w-3" />
                                    )}
                                  </button>
                                ) : null}
                              <div
                                className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border font-semibold text-xs ${getStatusColor(attendance.status)} cursor-pointer hover:opacity-80 transition-all hover:scale-105`}
                                  title={buildAttendanceTooltip(attendance, { includeUpdater: isAdmin })}
                              >
                                {getStatusLabel(attendance.status)}
                                </div>
                                {isAdmin && attendance.marked_by_name ? (
                                  <span
                                    className="w-full truncate text-center text-[9px] leading-tight text-gray-500 px-0.5"
                                    title={`Updated by ${attendance.marked_by_name}`}
                                  >
                                    {attendance.marked_by_name}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 text-gray-400 cursor-pointer hover:bg-gray-100 transition-all">
                                -
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4 border-t border-gray-200 px-4 pb-4 pt-4 text-sm">
            <span className="font-medium text-gray-500">Legend:</span>
            <div className="flex items-center gap-1.5">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-emerald-100 text-xs font-semibold text-emerald-700">
                P
              </span>
              <span className="text-gray-600">Present</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-rose-100 text-xs font-semibold text-rose-700">
                A
              </span>
              <span className="text-gray-600">Absent</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-amber-100 text-xs font-semibold text-amber-700">
                L
              </span>
              <span className="text-gray-600">Late</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-sky-100 text-xs font-semibold text-sky-700">
                E
              </span>
              <span className="text-gray-600">Excused</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-violet-100 text-xs font-semibold text-violet-700">
                H
              </span>
              <span className="text-gray-600">Holiday</span>
            </div>
            {isAdmin ? (
              <p className="w-full text-xs text-gray-500 sm:w-auto sm:ml-auto">
                Name under each marked day shows who last updated that attendance.
              </p>
            ) : null}
          </div>
        </div>
        )
      ) : (
        /* Summary Report View */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {attendanceReport.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              <HiOutlineUsers className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No attendance records found</p>
            </div>
          ) : (
            attendanceReport.map((report) => {
              const totalDays = (parseInt(report.present_days) || 0) + (parseInt(report.absent_days) || 0) + 
                               (parseInt(report.late_days) || 0) + (parseInt(report.excused_days) || 0);
              const attendanceRate = totalDays > 0 
                ? Math.round(((parseInt(report.present_days) || 0) + (parseInt(report.late_days) || 0)) / totalDays * 100) 
                : 0;
              
              const charges = calculatePatientCharges(report);
              
              return (
                <div key={report.id} className="card hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{report.full_name}</h3>
                      <p className="text-sm text-primary-600">{report.patient_code}</p>
                      {report.therapy_start_date && report.therapy_end_date && (
                        <p className="text-xs text-gray-500 mt-1">
                          Plan: {format(new Date(report.therapy_start_date), 'dd-MM-yyyy')} to {format(new Date(report.therapy_end_date), 'dd-MM-yyyy')}
                        </p>
                      )}
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      attendanceRate >= 80 ? 'bg-emerald-100 text-emerald-700' :
                      attendanceRate >= 60 ? 'bg-amber-100 text-amber-700' :
                      'bg-rose-100 text-rose-700'
                    }`}>
                      {attendanceRate}%
                    </div>
                  </div>
                  
                  {/* Attendance Progress Bar */}
                  <div className="h-2 bg-gray-100 rounded-full mb-4 overflow-hidden flex">
                    {totalDays > 0 && (
                      <>
                        <div 
                          className="bg-emerald-500 h-full" 
                          style={{ width: `${(parseInt(report.present_days) || 0) / totalDays * 100}%` }}
                        />
                        <div 
                          className="bg-amber-500 h-full" 
                          style={{ width: `${(parseInt(report.late_days) || 0) / totalDays * 100}%` }}
                        />
                        <div 
                          className="bg-sky-500 h-full" 
                          style={{ width: `${(parseInt(report.excused_days) || 0) / totalDays * 100}%` }}
                        />
                        <div 
                          className="bg-rose-500 h-full" 
                          style={{ width: `${(parseInt(report.absent_days) || 0) / totalDays * 100}%` }}
                        />
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                      <span className="text-gray-600">Present:</span>
                      <span className="font-semibold text-emerald-700">{report.present_days || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                      <span className="text-gray-600">Absent:</span>
                      <span className="font-semibold text-rose-700">{report.absent_days || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                      <span className="text-gray-600">Late:</span>
                      <span className="font-semibold text-amber-700">{report.late_days || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-sky-500"></span>
                      <span className="text-gray-600">Excused:</span>
                      <span className="font-semibold text-sky-700">{report.excused_days || 0}</span>
                    </div>
                  </div>

                  {/* Charges Calculation Section */}
                  {charges && (
                    <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
                      <div className="bg-primary-50 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Fee per Session:</span>
                          <span className="font-semibold text-gray-900">₹{charges.feePerSession.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Working Days (Current Month):</span>
                          <span className="font-semibold text-gray-900">{charges.workingDaysCurrentMonth} days</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Present Days:</span>
                          <span className="font-semibold text-emerald-700">{charges.presentDays} days</span>
                          {charges.presentSundays > 0 && (
                            <span className="text-xs text-gray-500">({charges.presentSundays} on Sundays)</span>
                          )}
                        </div>
                        
                        {/* Assumption Calculation */}
                        <div className="bg-white rounded-md p-2 mt-2 border border-primary-200">
                          <div className="text-xs font-medium text-gray-700 mb-1.5">Assumption:</div>
                          <div className="flex justify-between items-center text-xs text-gray-600">
                            <span>If {charges.presentDays} days present × ₹{charges.feePerSession.toFixed(2)}:</span>
                            <span className="font-semibold text-gray-900">₹{charges.chargesForPresentDays.toFixed(2)}</span>
                          </div>
                        </div>

                        {charges.remainingDaysNextMonth > 0 && (
                          <div className="flex justify-between items-center text-xs text-gray-500">
                            <span>Remaining Days (Next Month):</span>
                            <span>{charges.remainingDaysNextMonth} days</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Mark Attendance Modal */}
      {showMarkModal && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-5xl">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Mark Attendance</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    <HiOutlineCalendar className="w-4 h-4 inline mr-1" />
                    {markModalScope === 'month' && isAdmin
                      ? `${format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')} — full month`
                      : format(new Date(markingDate), 'EEEE, MMMM dd, yyyy')}
                  </p>
                  {isAdmin && markModalScope === 'month' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Use status below (e.g. Excused) for someone on leave the whole month.
                      {monthMarkSkipSundays ? ' Sundays are not marked.' : ''}
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:items-end gap-2">
                  {isAdmin && (
                    <div className="inline-flex rounded-lg border border-gray-200 bg-white/70 p-1">
                      <button
                        type="button"
                        onClick={() => setMarkModalScope('day')}
                        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                          markModalScope === 'day'
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        This day
                      </button>
                      <button
                        type="button"
                        onClick={() => setMarkModalScope('month')}
                        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                          markModalScope === 'month'
                            ? 'bg-gray-900 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        Full month
                      </button>
                    </div>
                  )}
                  {isAdmin && markModalScope === 'month' && (
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={monthMarkSkipSundays}
                        onChange={(e) => setMonthMarkSkipSundays(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Skip Sundays
                    </label>
                  )}
                  <div className="inline-flex rounded-lg border border-gray-200 bg-white/70 p-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (!markingDate) return;
                        if (!markModalPatientId) return;
                        setMarkModalMode('single');
                        setMarkModalSearch('');
                        setSelectAll(false);
                        const day = parseInt(String(markingDate).slice(-2), 10);
                        const records = buildMarkingRecords(day, markModalPatientId);
                        if (records.length) setMarkingRecords(records);
                      }}
                      className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                        markModalMode === 'single'
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                      disabled={!markModalPatientId}
                      title={!markModalPatientId ? 'No selected patient for this entry point' : 'Only this patient'}
                    >
                      Selected patient
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!markingDate) return;
                        setMarkModalMode('all');
                        setMarkModalSearch('');
                        setSelectAll(false);
                        const day = parseInt(String(markingDate).slice(-2), 10);
                        const records = buildMarkingRecords(day, null);
                        if (records.length) setMarkingRecords(records);
                      }}
                      className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                        markModalMode === 'all'
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                      title="All patients"
                    >
                      All patients
                    </button>
                  </div>

                  {markModalMode === 'all' ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleMarkAllPresent}
                    className="px-3 py-1.5 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
                  >
                    <HiOutlineCheckCircle className="w-4 h-4 inline mr-1" />
                    Mark All Present
                  </button>
                  <button
                    onClick={handleMarkAllAbsent}
                    className="px-3 py-1.5 text-sm bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 transition-colors"
                  >
                    <HiOutlineXCircle className="w-4 h-4 inline mr-1" />
                    Mark All Absent
                  </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <div className="mb-4">
                <label className="mb-1.5 block text-xs font-medium text-gray-500">Search patients</label>
                <div className="relative">
                  <HiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={markModalSearch}
                    onChange={(e) => setMarkModalSearch(e.target.value)}
                    placeholder="Name or patient code…"
                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                    autoComplete="off"
                  />
                </div>
                {markModalSearch.trim() ? (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Showing {filteredMarkingRecords.length} of {markingRecords.length} patients
                  </p>
                ) : null}
              </div>
              <div className="space-y-4">
                {filteredMarkingRecords.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-8 text-center text-sm text-gray-500">
                    No patients match your search.
                  </p>
                ) : (
                  filteredMarkingRecords.map((record) => (
                  <div 
                    key={record.patient_id} 
                    className={`border rounded-xl p-4 transition-all ${
                      record.status === 'present' ? 'border-emerald-200 bg-emerald-50/50' :
                      record.status === 'absent' ? 'border-rose-200 bg-rose-50/50' :
                      record.status === 'late' ? 'border-amber-200 bg-amber-50/50' :
                      record.status === 'excused' ? 'border-sky-200 bg-sky-50/50' :
                      'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <span className="font-semibold text-gray-900">{record.patient_name}</span>
                        <span className="ml-2 text-sm text-primary-600">{record.patient_code}</span>
                        {isAdmin && record.has_existing_attendance && record.marked_by_name ? (
                          <p className="mt-0.5 text-xs text-gray-500">
                            Last updated by <span className="font-medium text-gray-700">{record.marked_by_name}</span>
                          </p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {canDelete('attendance') && record.attendance_id ? (
                          <button
                            type="button"
                            disabled={clearingAttendanceId === record.attendance_id}
                            onClick={() => handleClearAttendance(record.attendance_id, record.patient_name)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                          >
                            <HiOutlineTrash className="h-3.5 w-3.5" />
                            Clear
                          </button>
                        ) : null}
                      {getStatusIcon(record.status)}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {/* Status */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Status</label>
                        <select
                          value={record.status}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMarkingRecords((prev) =>
                              prev.map((r) => (r.patient_id === record.patient_id ? { ...r, status: v } : r))
                            );
                          }}
                          className={`w-full px-3 py-2 border rounded-lg text-sm font-medium ${
                            record.status === 'present' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' :
                            record.status === 'absent' ? 'border-rose-300 bg-rose-50 text-rose-700' :
                            record.status === 'late' ? 'border-amber-300 bg-amber-50 text-amber-700' :
                            record.status === 'excused' ? 'border-sky-300 bg-sky-50 text-sky-700' :
                            'border-gray-300'
                          }`}
                        >
                          <option value="present">Present</option>
                          <option value="absent">Absent</option>
                          <option value="late">Late</option>
                          <option value="excused">Excused</option>
                          <option value="holiday">Holiday</option>
                        </select>
                      </div>

                      {/* Check In Time */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Check In</label>
                        <input
                          type="time"
                          value={record.check_in_time}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMarkingRecords((prev) =>
                              prev.map((r) => (r.patient_id === record.patient_id ? { ...r, check_in_time: v } : r))
                            );
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          disabled={record.status === 'absent' || record.status === 'holiday'}
                        />
                      </div>

                      {/* Check Out Time */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Check Out</label>
                        <input
                          type="time"
                          value={record.check_out_time}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMarkingRecords((prev) =>
                              prev.map((r) => (r.patient_id === record.patient_id ? { ...r, check_out_time: v } : r))
                            );
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          disabled={record.status === 'absent' || record.status === 'holiday'}
                        />
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Notes</label>
                        <input
                          type="text"
                          value={record.notes}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMarkingRecords((prev) =>
                              prev.map((r) => (r.patient_id === record.patient_id ? { ...r, notes: v } : r))
                            );
                          }}
                          placeholder="Optional notes"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="button"
                disabled={bulkSaveLoading}
                onClick={() => {
                  setShowMarkModal(false);
                  setMarkModalSearch('');
                }}
                className="w-full sm:w-auto px-5 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkMark}
                disabled={bulkSaveLoading}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {bulkSaveLoading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden />
                ) : null}
                {bulkSaveLoading
                  ? 'Saving…'
                  : markModalScope === 'month' && isAdmin
                    ? 'Save full month'
                    : 'Save Attendance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientAttendance;

